import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    fetchStatus: "idle",
    fetchByIdStatus: "idle",
    createStatus: "idle",
    updateStatus: "idle",
    deleteStatus: "idle",
    pagination: {},
    error: null,
};

// Fetch building list
export const fetchAllBuilding = createAsyncThunk('building/fetchAllBuilding',
    async ({scopeId, roleSign, page, limit}) => {
        try {
            const response = await api.get(`/${roleSign}/buildings?page=${page}&limit=${limit}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Create a new building
export const createBuilding = createAsyncThunk('building/createBuilding',
    async ({data, roleSign}) => {
        try {
            const response = await api.post(`/${roleSign}/buildings`, data);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch building by ID
export const fetchBuildingById = createAsyncThunk('building/fetchBuildingById',
    async ({id, roleSign}) => {
        try {
            const response = await api.get(`/${roleSign}/buildings/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Update building
export const updateBuilding = createAsyncThunk('building/updateBuilding',
    async ({id, data, roleSign}) => {
        try {
            const response = await api.patch(`/${roleSign}/buildings/${id}`, data);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Delete building
export const deleteBuilding = createAsyncThunk('building/deleteBuilding',
    async (id) => {
        try {
            const response = await api.delete(`/adg/member/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

const buildingSlice = createSlice({
    name: "building",
    initialState,
    reducers: {
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch building
            .addCase(fetchAllBuilding.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchAllBuilding.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchAllBuilding.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            // Create building
            .addCase(createBuilding.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createBuilding.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createBuilding.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch building by ID
            .addCase(fetchBuildingById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchBuildingById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchBuildingById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })

            // Update building
            .addCase(updateBuilding.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateBuilding.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateBuilding.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })

            // Delete employee
            .addCase(deleteBuilding.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteBuilding.fulfilled, (state, action) => {
                const {deleteStatus, error} = handleResponseStatus(action.payload, 'deleteStatus');
                const id = action.meta.arg.id;
                if (deleteStatus === "success") {
                    state.items = state.items.filter(item => item.id !== id);
                }
                state.deleteStatus = deleteStatus;
                state.error = error;
            })
            .addCase(deleteBuilding.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            });
    }
});

export const {
    resetUpdateStatus,
    resetDeleteStatus,
    resetCreateStatus,
} = buildingSlice.actions;

export default buildingSlice.reducer;
